import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import {
  CORRECTION_ONLY_EXCEPTIONS,
  EVENT_LABELS,
  EXCEPTION_LABELS,
  fmtDate,
  fmtDateTime,
  fmtTime,
  localDate,
  REASON_LABELS,
  type CorrectionDTO,
  type EventDTO,
  type ExceptionDTO,
  type TimesheetDetailDTO,
} from '@em/shared';
import { api, invalidateAttendance, useCategories, useCorrections, useJobs, useTimesheet } from '../../api';
import { useSession } from '../../auth/AuthContext';
import { CorrectionDialog, type CorrectionPrefill } from '../../components/CorrectionDialog';
import { hrs, minutesToHM } from '../../components/format';
import { PromptDialog } from '../../components/PromptDialog';
import { useToast } from '../../components/Toast';
import { Alert, Badge, Button, Card, EmptyState, Hours, PageHeader, QueryState, SeverityBadge, TimesheetBadge } from '../../components/ui';

export function TimesheetPage() {
  const { id = '' } = useParams();
  const q = useTimesheet(id);
  return <QueryState q={q} loadingLabel="Loading timesheet">{(ts) => <TimesheetView ts={ts} />}</QueryState>;
}

function TimesheetView({ ts }: { ts: TimesheetDetailDTO }) {
  const session = useSession();
  const toast = useToast();
  const tz = ts.timezone;
  const jobs = useJobs(session.features.jobCosting);
  const categories = useCategories();
  const corrections = useCorrections({ employeeId: ts.employeeId });
  const [busy, setBusy] = useState<string | null>(null);
  const [dialog, setDialog] = useState<'reject' | null>(null);
  const [correcting, setCorrecting] = useState<CorrectionPrefill | null>(null);
  const [dismissing, setDismissing] = useState<ExceptionDTO | null>(null);

  const jobCode = (jid: string | null) => (jid ? (jobs.data?.find((j) => j.id === jid)?.code ?? 'Job') : null);
  const catName = (code: string) => categories.data?.find((c) => c.code === code)?.name ?? code;
  const periodOpen = ts.period.status === 'open';
  const isOwn = session.user.employeeId === ts.employeeId;

  const act = async (key: string, fn: () => Promise<unknown>, success: string) => {
    setBusy(key);
    try {
      await fn();
      invalidateAttendance();
      toast.success(success);
    } catch (e) {
      toast.error(e);
    } finally {
      setBusy(null);
    }
  };

  const linesByDate = useMemo(() => {
    const m = new Map<string, typeof ts.lines>();
    for (const l of ts.lines) m.set(l.date, [...(m.get(l.date) ?? []), l]);
    return [...m.entries()].sort(([a], [b]) => a.localeCompare(b));
  }, [ts.lines]);

  const categoryTotals = Object.entries(ts.totals.byCategory).sort(([a], [b]) => a.localeCompare(b));

  return (
    <>
      <PageHeader
        breadcrumb={
          <>
            <Link to="/periods">Pay periods</Link> / <Link to={`/periods/${ts.payPeriodId}`}>{fmtDate(ts.period.startDate)} – {fmtDate(ts.period.endDate)}</Link>
          </>
        }
        title={
          <span className="row">
            {ts.employeeName} <TimesheetBadge status={ts.status} />
          </span>
        }
        subtitle={
          <>
            Employee {ts.employeeNumber} · times in {tz} · calculation v{ts.calcVersion}
            {ts.approvedByName && ` · approved by ${ts.approvedByName}${ts.approvedAt ? ` ${fmtDateTime(ts.approvedAt, tz)}` : ''}`}
          </>
        }
        actions={
          <>
            {periodOpen && (
              <Button onClick={() => setCorrecting({ employeeId: ts.employeeId, date: ts.period.startDate, timezone: tz })}>Correct punches</Button>
            )}
            {periodOpen && (
              <Button loading={busy === 'recalc'} onClick={() => act('recalc', () => api.recalculateTimesheet(ts.id), 'Timesheet recalculated')}>
                Recalculate
              </Button>
            )}
            {ts.status === 'approved' && (
              <Button loading={busy === 'withdraw'} onClick={() => act('withdraw', () => api.withdrawApproval(ts.id), 'Approval withdrawn')}>
                Withdraw approval
              </Button>
            )}
            {['ready', 'needs_attention', 'approved'].includes(ts.status) && (
              <Button variant="danger" onClick={() => setDialog('reject')}>
                Send back
              </Button>
            )}
            {(ts.status === 'ready' || ts.status === 'needs_attention') && (
              <Button
                variant="primary"
                disabled={ts.status !== 'ready'}
                title={ts.status !== 'ready' ? 'Resolve the blocking exceptions first' : isOwn ? 'You cannot approve your own timesheet' : undefined}
                loading={busy === 'approve'}
                onClick={() => act('approve', () => api.approveTimesheet(ts.id), 'Timesheet approved')}
              >
                Approve
              </Button>
            )}
          </>
        }
      />

      {ts.status === 'rejected' && ts.rejectedNote && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="amber" title="Sent back for correction">
            {ts.rejectedNote}
          </Alert>
        </div>
      )}
      {isOwn && (
        <div style={{ marginBottom: 12 }}>
          <Alert tone="accent">This is your own timesheet. Another approver must approve it and its corrections.</Alert>
        </div>
      )}

      <div className="grid grid-4" style={{ marginBottom: 16 }}>
        <div className="card stat">
          <div className="label">Paid hours</div>
          <div className="value">
            <Hours value={ts.totals.paidHours} />
          </div>
        </div>
        <div className="card stat">
          <div className="label">Worked hours</div>
          <div className="value">
            <Hours value={ts.totals.workedHours} />
          </div>
        </div>
        <div className="card stat">
          <div className="label">Blocking</div>
          <div className={`value ${ts.openBlocking ? 'red' : ''}`}>{ts.openBlocking}</div>
        </div>
        <div className="card stat">
          <div className="label">To review</div>
          <div className={`value ${ts.openReview ? 'amber' : ''}`}>{ts.openReview}</div>
        </div>
      </div>

      <ExceptionsCard
        ts={ts}
        onCorrect={(x) => setCorrecting({ employeeId: ts.employeeId, date: x.date, eventIds: x.eventIds, timezone: tz })}
        onDismiss={setDismissing}
        canAct={periodOpen}
      />

      {ts.diff && ts.diff.length > 0 && (
        <Card title="What changed in the last recalculation" className="section" tight>
          <table className="table compact">
            <thead>
              <tr>
                <th>Date</th>
                <th>Category</th>
                {session.features.jobCosting && <th>Job</th>}
                <th className="num">Before</th>
                <th className="num">After</th>
                <th className="num">Change</th>
              </tr>
            </thead>
            <tbody>
              {ts.diff.map((d, i) => {
                const delta = Math.round((d.after - d.before) * 100) / 100;
                return (
                  <tr key={i}>
                    <td className="nowrap">{fmtDate(d.date)}</td>
                    <td>{d.category}</td>
                    {session.features.jobCosting && <td>{jobCode(d.jobId) ?? '–'}</td>}
                    <td className="num">{hrs(d.before)}</td>
                    <td className="num">{hrs(d.after)}</td>
                    <td className={`num strong ${delta > 0 ? 'diff-up' : 'diff-down'}`}>
                      {delta > 0 ? '+' : ''}
                      {hrs(delta)}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      <Card title="Shifts" className="section" tight>
        {ts.shifts.length === 0 ? (
          <EmptyState title="No shifts recorded in this period" />
        ) : (
          <div className="table-wrap">
            <table className="table">
              <thead>
                <tr>
                  <th>Date</th>
                  <th>In</th>
                  <th>Out</th>
                  <th>Breaks</th>
                  <th className="num">Worked</th>
                  <th className="num">Paid</th>
                  <th>Roster</th>
                  <th>Status</th>
                </tr>
              </thead>
              <tbody>
                {ts.shifts.map((s, i) => {
                  const r = s.rosterShiftId ? ts.roster.find((x) => x.id === s.rosterShiftId) : undefined;
                  const startDiff = r ? Math.round((Date.parse(s.startAt) - Date.parse(r.startAt)) / 60000) : null;
                  return (
                    <tr key={i}>
                      <td className="nowrap strong">{fmtDate(s.date)}</td>
                      <td className="nowrap">{fmtTime(s.startAt, s.tz || tz)}</td>
                      <td className="nowrap">{s.endAt ? fmtTime(s.endAt, s.tz || tz) : <Badge tone="red">No clock-out</Badge>}</td>
                      <td>
                        {s.breaks.length === 0 ? (
                          <span className="muted">None</span>
                        ) : (
                          s.breaks.map((b, j) => (
                            <div key={j} className="small nowrap">
                              {b.kind === 'meal' ? 'Meal' : 'Rest'} {fmtTime(b.startAt, s.tz || tz, false)}–{b.endAt ? fmtTime(b.endAt, s.tz || tz, false) : '?'}
                              {b.paid ? ' (paid)' : ''}
                            </div>
                          ))
                        )}
                      </td>
                      <td className="num" title={minutesToHM(s.workedMinutes)}>
                        {hrs(s.workedMinutes / 60)}
                      </td>
                      <td className="num" title={minutesToHM(s.paidMinutes)}>
                        {hrs(s.paidMinutes / 60)}
                      </td>
                      <td className="small">
                        {r ? (
                          <>
                            {fmtTime(r.startAt, tz, false)}–{r.endAt ? fmtTime(r.endAt, tz, false) : 'open'}
                            {startDiff !== null && startDiff !== 0 && (
                              <div className={startDiff > 0 ? 'amber' : 'muted'}>
                                {startDiff > 0 ? `${startDiff} min late` : `${Math.abs(startDiff)} min early`}
                              </div>
                            )}
                          </>
                        ) : (
                          <span className="muted">Not rostered</span>
                        )}
                      </td>
                      <td>
                        {s.status === 'complete' ? <Badge tone="green">Complete</Badge> : s.status === 'in_progress' ? <Badge tone="accent">In progress</Badge> : <Badge tone="red">Broken</Badge>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <div className="grid section" style={{ gridTemplateColumns: 'minmax(0, 2fr) minmax(260px, 1fr)' }}>
        <Card title="Calculated lines" tight>
          {linesByDate.length === 0 ? (
            <EmptyState title="No calculated lines" />
          ) : (
            <div className="table-wrap">
              <table className="table compact">
                <thead>
                  <tr>
                    <th>Category</th>
                    <th className="num">Hours</th>
                    <th>Rule version</th>
                    {session.features.jobCosting && <th>Job</th>}
                    <th />
                  </tr>
                </thead>
                <tbody>
                  {linesByDate.map(([date, lines]) => (
                    <LineGroup key={date} date={date} lines={lines} catName={catName} jobCode={jobCode} jobCosting={session.features.jobCosting} />
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </Card>
        <Card title="Totals by category" tight>
          <table className="table compact">
            <tbody>
              {categoryTotals.map(([c, h]) => (
                <tr key={c}>
                  <td>
                    <strong>{c}</strong> <span className="muted small">{catName(c) !== c ? catName(c) : ''}</span>
                  </td>
                  <td className="num">
                    {categories.data?.find((x) => x.code === c)?.unit === 'units' ? `${h} unit${h === 1 ? '' : 's'}` : <Hours value={h} />}
                  </td>
                </tr>
              ))}
              <tr className="total">
                <td>Paid hours</td>
                <td className="num">
                  <Hours value={ts.totals.paidHours} />
                </td>
              </tr>
            </tbody>
          </table>
        </Card>
      </div>

      <EventsCard events={ts.events} corrections={corrections.data ?? []} tz={tz} jobCode={jobCode} periodStart={ts.period.startDate} periodEnd={ts.period.endDate} />

      {correcting && <CorrectionDialog prefill={correcting} onClose={() => setCorrecting(null)} />}
      {dialog === 'reject' && (
        <PromptDialog
          title="Send timesheet back"
          description={`${ts.employeeName} is notified with your note. The timesheet returns to Ready after a correction or recalculation.`}
          label="What needs fixing?"
          required
          minLength={3}
          danger
          confirmLabel="Send back"
          onClose={() => setDialog(null)}
          onConfirm={async (note) => {
            await api.rejectTimesheet(ts.id, note);
            invalidateAttendance();
            toast.success('Timesheet sent back');
          }}
        />
      )}
      {dismissing && (
        <PromptDialog
          title="Dismiss exception"
          description={`${EXCEPTION_LABELS[dismissing.type]} on ${fmtDate(dismissing.date)}: ${dismissing.message}`}
          label="Why is this acceptable?"
          required
          confirmLabel="Dismiss"
          onClose={() => setDismissing(null)}
          onConfirm={async (note) => {
            await api.dismissException(dismissing.id, note);
            invalidateAttendance();
            toast.success('Exception dismissed');
          }}
        />
      )}
    </>
  );
}

function LineGroup({
  date,
  lines,
  catName,
  jobCode,
  jobCosting,
}: {
  date: string;
  lines: TimesheetDetailDTO['lines'];
  catName: (c: string) => string;
  jobCode: (j: string | null) => string | null;
  jobCosting: boolean;
}) {
  const total = lines.filter((l) => l.unit === 'hours').reduce((s, l) => s + l.hours, 0);
  return (
    <>
      <tr className="group">
        <td>{fmtDate(date)}</td>
        <td className="num">
          <Hours value={Math.round(total * 100) / 100} />
        </td>
        <td colSpan={jobCosting ? 3 : 2} />
      </tr>
      {lines.map((l) => (
        <tr key={l.id}>
          <td>
            <strong>{l.category}</strong> <span className="muted small">{catName(l.category) !== l.category ? catName(l.category) : ''}</span>
          </td>
          <td className={`num ${l.hours < 0 ? 'red' : ''}`}>{l.unit === 'units' ? `${l.hours} unit${l.hours === 1 ? '' : 's'}` : <Hours value={l.hours} />}</td>
          <td className="small muted">{l.ruleRef}</td>
          {jobCosting && <td>{jobCode(l.jobId) ?? <span className="muted">–</span>}</td>}
          <td>{l.isAdjustment && <Badge tone="amber">Adjustment{l.adjustmentForDate ? ` for ${fmtDate(l.adjustmentForDate)}` : ''}</Badge>}</td>
        </tr>
      ))}
    </>
  );
}

function ExceptionsCard({ ts, onCorrect, onDismiss, canAct }: { ts: TimesheetDetailDTO; onCorrect: (x: ExceptionDTO) => void; onDismiss: (x: ExceptionDTO) => void; canAct: boolean }) {
  const [showClosed, setShowClosed] = useState(false);
  const list = ts.exceptions.filter((x) => showClosed || x.status === 'open');
  const closed = ts.exceptions.length - ts.exceptions.filter((x) => x.status === 'open').length;
  return (
    <Card
      title={`Exceptions (${ts.exceptions.filter((x) => x.status === 'open').length} open)`}
      tight
      actions={
        closed > 0 && (
          <Button size="sm" variant="ghost" onClick={() => setShowClosed((s) => !s)}>
            {showClosed ? 'Hide' : 'Show'} {closed} closed
          </Button>
        )
      }
    >
      {list.length === 0 ? (
        <EmptyState title="No open exceptions on this timesheet" />
      ) : (
        <table className="table">
          <tbody>
            {list.map((x) => (
              <tr key={x.id} className={x.status !== 'open' ? 'struck' : ''}>
                <td className="nowrap">{fmtDate(x.date)}</td>
                <td className="nowrap strong">{EXCEPTION_LABELS[x.type]}</td>
                <td>
                  <SeverityBadge severity={x.severity} />
                </td>
                <td>
                  {x.message}
                  {x.status !== 'open' && (
                    <div className="small muted">
                      {x.status === 'dismissed' ? 'Dismissed' : 'Resolved'}
                      {x.resolvedByName ? ` by ${x.resolvedByName}` : ''}
                      {x.resolutionNote ? `: ${x.resolutionNote}` : ''}
                    </div>
                  )}
                </td>
                <td className="actions">
                  {x.status === 'open' && canAct && (
                    <>
                      <Button size="sm" variant="primary" onClick={() => onCorrect(x)}>
                        Correct punches
                      </Button>
                      {!CORRECTION_ONLY_EXCEPTIONS.includes(x.type) && (
                        <Button size="sm" onClick={() => onDismiss(x)}>
                          Dismiss
                        </Button>
                      )}
                    </>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

/**
 * Every punch in the period, including superseded and voided ones, so the
 * audit trail is visible: originals are struck through with who changed them.
 */
function EventsCard({
  events,
  corrections,
  tz,
  jobCode,
  periodStart,
  periodEnd,
}: {
  events: EventDTO[];
  corrections: CorrectionDTO[];
  tz: string;
  jobCode: (j: string | null) => string | null;
  periodStart: string;
  periodEnd: string;
}) {
  const replacement = new Map(events.filter((e) => e.supersedesId).map((e) => [e.supersedesId!, e]));
  const voidedBy = new Map<string, CorrectionDTO>();
  for (const c of corrections) if (c.status === 'approved') for (const ch of c.changes) if (ch.action === 'void') voidedBy.set(ch.eventId, c);
  const corrById = new Map(corrections.map((c) => [c.id, c]));
  const superseded = events.filter((e) => e.status !== 'active').length;

  return (
    <Card title={`Punch record (${events.length}${superseded ? `, ${superseded} corrected` : ''})`} className="section" tight>
      {events.length === 0 ? (
        <EmptyState title="No punches in this period" />
      ) : (
        <div className="table-wrap">
          <table className="table compact">
            <thead>
              <tr>
                <th>Date</th>
                <th>Punch</th>
                <th>Time</th>
                <th>Source</th>
                <th>Received</th>
                <th>Correction trail</th>
              </tr>
            </thead>
            <tbody>
              {events.map((e) => {
                const rep = replacement.get(e.id);
                const voidC = voidedBy.get(e.id);
                const ownC = e.correctionId ? corrById.get(e.correctionId) : undefined;
                const day = localDate(e.occurredAt, tz);
                const outside = day < periodStart || day > periodEnd;
                return (
                  <tr key={e.id} className={e.status !== 'active' ? 'struck' : ''} style={outside ? { opacity: 0.6 } : undefined}>
                    <td className="strike nowrap">
                      {fmtDate(day)}
                      {outside && <div className="small muted">Outside this period</div>}
                    </td>
                    <td className="strike nowrap">
                      {EVENT_LABELS[e.type]}
                      {e.breakKind ? ` (${e.breakKind})` : ''}
                      {jobCode(e.jobId) ? ` · ${jobCode(e.jobId)}` : ''}
                    </td>
                    <td className="strike nowrap">{fmtTime(e.occurredAt, tz)}</td>
                    <td className="nowrap">
                      {e.source}
                      {e.hasPhoto && <PhotoLink eventId={e.id} />}
                      {e.clockSkewSeconds && Math.abs(e.clockSkewSeconds) > 120 ? (
                        <>
                          {' '}
                          <Badge tone="amber">Clock skew {Math.round(e.clockSkewSeconds / 60)} min</Badge>
                        </>
                      ) : null}
                    </td>
                    <td className="small muted nowrap">{fmtDateTime(e.receivedAt, tz)}</td>
                    <td className="small">
                      {e.status === 'superseded' && (
                        <span>
                          <Badge>Superseded</Badge> {rep ? `changed to ${fmtTime(rep.occurredAt, tz, false)} by ${rep.createdByName ?? 'unknown'}` : 'replaced'}
                          {rep?.reasonCode && ` · ${REASON_LABELS[rep.reasonCode]}`}
                          {rep?.note && ` · “${rep.note}”`}
                        </span>
                      )}
                      {e.status === 'voided' && (
                        <span>
                          <Badge tone="red">Voided</Badge>
                          {voidC ? ` by ${voidC.decidedByName ?? 'unknown'} · ${REASON_LABELS[voidC.reasonCode]}${voidC.note ? ` · “${voidC.note}”` : ''}` : ''}
                        </span>
                      )}
                      {e.status === 'active' && e.source === 'manual' && (
                        <span>
                          <Badge tone="accent">{e.supersedesId ? 'Corrected' : 'Added'}</Badge> by {e.createdByName ?? 'unknown'}
                          {e.reasonCode && ` · ${REASON_LABELS[e.reasonCode]}`}
                          {e.note && ` · “${e.note}”`}
                          {ownC && ownC.requestedByName !== e.createdByName && <span className="muted"> · requested by {ownC.requestedByName}</span>}
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}

function PhotoLink({ eventId }: { eventId: string }) {
  const [url, setUrl] = useState<string | null>(null);
  return (
    <>
      {' '}
      <button
        className="btn ghost sm"
        onClick={async () => setUrl(url ? null : await api.downloadUrl(`/api/photos/${eventId}`))}
        aria-expanded={!!url}
      >
        Photo
      </button>
      {url && (
        <div>
          <img src={url} alt="Punch photo" style={{ width: 120, borderRadius: 4, marginTop: 4 }} />
        </div>
      )}
    </>
  );
}
